import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ChevronLeft, ChevronRight, Loader2, Menu, RefreshCw, Save, Tag } from 'lucide-react';
import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import { useUIStore } from '@/store/uiStore';
import { channelPricingApi } from '@/services/api';
import { resolveFileUrl } from '@/utils/fileUrl';

const PAGE_SIZE = 15;

type ChannelRow = {
  id: string;
  title: string;
  maturity: string;
  unlocked: boolean;
  can_price: boolean;
};

type CategoryRow = {
  name: string;
  category_name?: string;
};

type ItemRow = {
  name: string;
  item_code?: string;
  item_name?: string;
  image?: string;
  category?: string;
  category_name?: string;
  sale_price?: number;
  published?: boolean;
  channel_price?: number | null;
  channel_delivery_price?: number | null;
  effective_price?: number;
};

function savedPriceText(it: ItemRow): string {
  return it.channel_price != null && it.channel_price !== undefined ? String(it.channel_price) : '';
}

export default function ChannelPricingPage() {
  const { toggleSidebar } = useUIStore();
  const qc = useQueryClient();
  const [channel, setChannel] = useState('shopify');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [category, setCategory] = useState('');
  const [publishedOnly, setPublishedOnly] = useState(true);
  const [offset, setOffset] = useState(0);
  /** Draft values keyed by item name — kept across pages until Save. */
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  /** Original saved text when first edited (for dirty detection across pages). */
  const [baselines, setBaselines] = useState<Record<string, string>>({});

  // Debounce search so typing doesn't thrash the API
  useEffect(() => {
    const t = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setOffset(0);
    }, 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const channelsQ = useQuery({
    queryKey: ['channelPricingChannels'],
    queryFn: async () =>
      ((await channelPricingApi.listChannels()).data.message?.channels || []) as ChannelRow[],
  });

  const available = useMemo(
    () => (channelsQ.data || []).filter((c) => c.can_price || c.unlocked),
    [channelsQ.data],
  );

  const activeChannel = available.some((c) => c.id === channel)
    ? channel
    : available[0]?.id || 'shopify';

  const catalogueQ = useQuery({
    queryKey: ['channelCatalogue', activeChannel, search, category, publishedOnly, offset],
    enabled: Boolean(activeChannel) && available.some((c) => c.id === activeChannel && c.can_price),
    queryFn: async () =>
      (
        await channelPricingApi.listCatalogue({
          channel: activeChannel,
          search: search || undefined,
          category: category || undefined,
          published_only: publishedOnly ? 1 : 0,
          limit: PAGE_SIZE,
          offset,
        })
      ).data.message as {
        items: ItemRow[];
        categories: CategoryRow[];
        channel: string;
        total: number;
        limit: number;
        offset: number;
        has_next_page: boolean;
        has_prev_page: boolean;
      },
  });

  const items = catalogueQ.data?.items || [];
  const categories = catalogueQ.data?.categories || [];
  const total = catalogueQ.data?.total ?? 0;
  const hasNext = Boolean(catalogueQ.data?.has_next_page);
  const hasPrev = Boolean(catalogueQ.data?.has_prev_page) || offset > 0;
  const pageFrom = total === 0 ? 0 : offset + 1;
  const pageTo = Math.min(offset + items.length, total);

  const dirtyItems = useMemo(() => {
    const out: Array<{ item: string; value: string }> = [];
    for (const [itemName, value] of Object.entries(drafts)) {
      const baseline = baselines[itemName];
      if (baseline === undefined) continue;
      if (value === baseline) continue;
      out.push({ item: itemName, value });
    }
    return out;
  }, [drafts, baselines]);

  const setDraft = (item: ItemRow, value: string) => {
    setBaselines((b) => (b[item.name] !== undefined ? b : { ...b, [item.name]: savedPriceText(item) }));
    setDrafts((d) => ({ ...d, [item.name]: value }));
  };

  const clearEdits = () => {
    setDrafts({});
    setBaselines({});
  };

  const saveAllMut = useMutation({
    mutationFn: async () => {
      if (!dirtyItems.length) throw new Error('No price changes to save');
      const payloadItems: Array<{
        item: string;
        channel_price?: number;
        clear_price?: number;
      }> = [];
      for (const row of dirtyItems) {
        const raw = row.value.trim();
        if (raw === '') {
          payloadItems.push({ item: row.item, clear_price: 1 });
          continue;
        }
        const n = Number(raw);
        if (Number.isNaN(n) || n < 0) {
          throw new Error('Invalid price for an item — use a number ≥ 0 or leave blank');
        }
        payloadItems.push({ item: row.item, channel_price: n });
      }
      return channelPricingApi.setPrices({
        channel: activeChannel,
        items: payloadItems,
        push: 1,
      });
    },
    onSuccess: (res) => {
      const msg = res.data.message as { updated?: number; errors?: unknown[] };
      const updated = msg?.updated ?? dirtyItems.length;
      const errCount = msg?.errors?.length || 0;
      if (errCount) {
        toast.error(`Saved ${updated}; ${errCount} failed`);
      } else {
        toast.success(`Saved ${updated} price${updated === 1 ? '' : 's'}`);
      }
      clearEdits();
      void qc.invalidateQueries({ queryKey: ['channelCatalogue'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.message || err.message),
  });

  const activeMeta = available.find((c) => c.id === activeChannel);
  const showDelivery = activeChannel === 'storefront';
  const dirtyCount = dirtyItems.length;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <TrialBanner />
        <header className="bg-white border-b border-gray-200 px-4 py-3 space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <button type="button" onClick={toggleSidebar} className="lg:hidden p-2 rounded-lg hover:bg-gray-100">
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Tag className="w-5 h-5 text-teal-700" />
                Channel Pricing
              </h1>
              <p className="text-sm text-gray-500">
                Override POS sale price per marketplace. Leave blank to use the POS price.
              </p>
            </div>
            <button
              type="button"
              className="btn-secondary text-sm inline-flex items-center gap-1.5"
              onClick={() => void catalogueQ.refetch()}
            >
              <RefreshCw className={`w-4 h-4 ${catalogueQ.isFetching ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              type="button"
              className="btn-primary text-sm inline-flex items-center gap-1.5"
              disabled={!dirtyCount || saveAllMut.isPending || !activeMeta?.can_price}
              onClick={() => saveAllMut.mutate()}
            >
              {saveAllMut.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save{dirtyCount ? ` (${dirtyCount})` : ''}
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {available.map((c) => {
              const active = activeChannel === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  disabled={!c.can_price}
                  onClick={() => {
                    setChannel(c.id);
                    setOffset(0);
                    clearEdits();
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                    active
                      ? 'bg-teal-700 text-white'
                      : !c.can_price
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white border border-gray-200 text-gray-700 hover:border-teal-600'
                  }`}
                  title={!c.can_price ? 'Add-on required or channel not live yet' : c.title}
                >
                  {c.title}
                  {!c.can_price ? ' · Locked' : c.maturity === 'scaffold' ? ' · Soon' : ''}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <input
              className="input-field text-sm flex-1 min-w-[12rem] max-w-md"
              placeholder="Search items…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <select
              className="input-field text-sm min-w-[10rem]"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setOffset(0);
              }}
            >
              <option value="">All categories</option>
              {categories.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.category_name || c.name}
                </option>
              ))}
            </select>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={publishedOnly}
                onChange={(e) => {
                  setPublishedOnly(e.target.checked);
                  setOffset(0);
                }}
              />
              Published on channel only
            </label>
            {activeChannel === 'storefront' ? (
              <Link to="/public-store?tab=catalogue" className="text-sm text-teal-700 hover:underline">
                Open Public Store catalogue →
              </Link>
            ) : null}
          </div>
        </header>

        <div className="flex-1 min-h-0 overflow-auto p-4">
          {!activeMeta?.can_price ? (
            <div className="bg-white border border-dashed border-gray-200 rounded-xl p-10 text-center text-gray-500">
              <p className="font-medium text-gray-800">Channel not available</p>
              <p className="text-sm mt-1">
                Unlock the add-on from Marketplaces, or wait until this connector goes live.
              </p>
              <Link to="/settings?tab=marketplaces" className="text-teal-700 text-sm mt-3 inline-block">
                Open Marketplaces
              </Link>
            </div>
          ) : catalogueQ.isLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-gray-700 border-b">
                    <tr>
                      <th className="px-4 py-3">Item</th>
                      <th className="px-4 py-3">Category</th>
                      <th className="px-4 py-3 text-right">POS price</th>
                      <th className="px-4 py-3 text-right">{activeMeta.title} price</th>
                      {showDelivery ? <th className="px-4 py-3 text-right">Delivery price</th> : null}
                      <th className="px-4 py-3 text-right">Effective</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {items.map((it) => {
                      const draft =
                        drafts[it.name] !== undefined ? drafts[it.name] : savedPriceText(it);
                      const isDirty =
                        drafts[it.name] !== undefined &&
                        baselines[it.name] !== undefined &&
                        drafts[it.name] !== baselines[it.name];
                      return (
                        <tr key={it.name} className={`hover:bg-gray-50 ${isDirty ? 'bg-amber-50/60' : ''}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 min-w-0">
                              {it.image ? (
                                <img
                                  src={resolveFileUrl(it.image)}
                                  alt=""
                                  className="w-9 h-9 rounded object-cover bg-gray-100 shrink-0"
                                />
                              ) : (
                                <div className="w-9 h-9 rounded bg-gray-100 shrink-0" />
                              )}
                              <div className="min-w-0">
                                <div className="font-medium text-gray-900 truncate">{it.item_name}</div>
                                <div className="text-xs text-gray-500">
                                  {it.item_code}
                                  {it.published ? '' : ' · not published'}
                                  {isDirty ? ' · unsaved' : ''}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-600 max-w-[10rem]">
                            <span className="truncate block">{it.category_name || '—'}</span>
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-gray-600">
                            {Number(it.sale_price || 0).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <input
                              className={`input-field text-sm w-28 ml-auto text-right ${
                                isDirty ? 'border-amber-400 ring-1 ring-amber-200' : ''
                              }`}
                              inputMode="decimal"
                              placeholder="POS"
                              value={draft}
                              onChange={(e) => setDraft(it, e.target.value)}
                            />
                          </td>
                          {showDelivery ? (
                            <td className="px-4 py-3 text-right tabular-nums text-gray-500">
                              {it.channel_delivery_price != null
                                ? Number(it.channel_delivery_price).toFixed(2)
                                : '—'}
                            </td>
                          ) : null}
                          <td className="px-4 py-3 text-right font-semibold tabular-nums">
                            {Number(it.effective_price || 0).toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                    {!items.length && (
                      <tr>
                        <td
                          colSpan={showDelivery ? 6 : 5}
                          className="px-4 py-12 text-center text-gray-500"
                        >
                          No items for this filter. Publish products on the channel from Items, then set
                          overrides here.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="px-4 py-3 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-gray-500">
                  {total === 0 ? '0 items' : `Showing ${pageFrom}–${pageTo} of ${total}`}
                  {dirtyCount ? ` · ${dirtyCount} unsaved` : ''}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn-secondary text-sm inline-flex items-center gap-1"
                    disabled={!hasPrev || catalogueQ.isFetching}
                    onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </button>
                  <button
                    type="button"
                    className="btn-secondary text-sm inline-flex items-center gap-1"
                    disabled={!hasNext || catalogueQ.isFetching}
                    onClick={() => setOffset((o) => o + PAGE_SIZE)}
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
